import {useEffect, useRef, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ArrowLeft, Download} from "lucide-react";
import {Link, useParams} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime} from "../../shared/utils/format";
import {useTranslation} from "../../shared/i18n";
import {downloadSettlementExport, getSettlementExport, settlementKeys} from "./api";
import "./settlements.css";

const RENDER_TONE = {
    pending: "neutral",
    rendering: "warning",
    finalized: "success",
    failed: "danger",
};

const renderTone = (status) => RENDER_TONE[status] || "neutral";
const renderLabel = (status, t) => t(`money.settlements.exports.render.${RENDER_TONE[status] ? status : "unknown"}`);

const byteSize = (value, t) => {
    if (value === null || value === undefined) return "-";
    return t("money.settlements.exports.detail.bytes", {
        size: new Intl.NumberFormat("ru-RU").format(value),
    });
};

export const SettlementExportDetailPage = () => {
    const {t} = useTranslation();
    const {exportId} = useParams();
    const numericExportId = /^[1-9]\d*$/.test(exportId || "") ? Number(exportId) : null;
    const capabilities = useCapabilities();
    const [countryScope, setCountryScope] = useState(() => resolveAdminCountryScope({
        role: capabilities.user?.role,
        allowedCountryIds: capabilities.scope?.country_ids || [],
        storedScope: localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
    }));
    const activeCountryScopeRef = useRef(countryScope);
    const [downloadResult, setDownloadResult] = useState(null);

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId == null ? null : String(event.detail.countryId);
            activeCountryScopeRef.current = nextScope;
            setCountryScope(nextScope);
            setDownloadResult(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () => window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const exportQuery = useQuery({
        queryKey: settlementKeys.exportDetail(countryScope, numericExportId),
        queryFn: () => getSettlementExport({exportId: numericExportId}),
        enabled: numericExportId !== null && countryScope !== null,
        retry: false,
    });

    const downloadMutation = useMutation({
        mutationFn: ({requestExportId}) => downloadSettlementExport({exportId: requestExportId}),
        onSuccess: (response, variables) => {
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setDownloadResult({kind: "success", message: t("money.settlements.exports.detail.downloaded", {filename: response.filename})});
        },
        onError: (error, variables) => {
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setDownloadResult(mapFinanceError(error));
        },
    });

    if (numericExportId === null) {
        return <DataState title={t("money.settlements.exports.detail.invalidTitle")} copy={t("money.settlements.exports.detail.invalidCopy")} />;
    }
    if (exportQuery.isLoading) {
        return <div className="admin-table-skeleton" aria-label={t("money.settlements.exports.detail.loading")}><span /><span /><span /></div>;
    }
    if (exportQuery.isError || !exportQuery.data) {
        return <DataState title={t("money.settlements.exports.detail.loadError")} copy={mapFinanceError(exportQuery.error).message} />;
    }

    const record = exportQuery.data;
    const renderStatus = renderLabel(record.render_status, t);
    return (
        <div className="admin-page settlement-export-detail-page">
            <Link className="settlement-back-link" to="/settlements/exports"><ArrowLeft aria-hidden="true" size={16} /> {t("money.settlements.exports.detail.back")}</Link>
            <header className="admin-page-header settlement-detail-header">
                <div>
                    <p className="admin-eyebrow">{t("money.settlements.exports.detail.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("money.settlements.exports.detail.title", {id: record.id})}</h1>
                    <p className="admin-page-copy">{formatDateTime(record.period_start)} - {formatDateTime(record.period_end)}</p>
                </div>
                <StatusBadge tone={renderTone(record.render_status)}>{renderStatus}</StatusBadge>
            </header>

            <MutationResultBanner result={downloadResult} />

            <section className="settlement-section" aria-labelledby="settlement-export-record-title">
                <h2 id="settlement-export-record-title">{t("money.settlements.exports.detail.recordTitle")}</h2>
                <DetailGrid items={[
                    {label: t("money.settlements.exports.detail.country"), value: record.country_id ? `#${record.country_id}` : "-"},
                    {label: t("money.settlements.exports.detail.operator"), value: record.created_by_id ? `#${record.created_by_id}` : "-"},
                    {label: t("money.settlements.exports.detail.format"), value: record.format.toUpperCase()},
                    {label: t("money.settlements.exports.detail.recordStatus"), value: record.status},
                    {label: t("money.settlements.exports.detail.created"), value: formatDateTime(record.created_at)},
                    {label: t("money.settlements.exports.detail.finalized"), value: formatDateTime(record.finalized_at)},
                    {label: t("money.settlements.exports.detail.size"), value: byteSize(record.size_bytes, t)},
                    {label: "SHA-256", value: record.sha256 || "-"},
                    {label: t("money.settlements.exports.detail.filename"), value: record.filename || "-"},
                ]} />
                {record.downloadable ? (
                    <button className="admin-button admin-button-primary settlement-download-button" type="button" disabled={downloadMutation.isPending} onClick={() => {
                        setDownloadResult(null);
                        downloadMutation.mutate({requestScope: countryScope, requestExportId: record.id});
                    }}>
                        <Download aria-hidden="true" size={16} /> {t("money.settlements.exports.detail.download")}
                    </button>
                ) : null}
            </section>

            <section className="settlement-section settlement-export-evidence" aria-labelledby="settlement-export-included-title">
                <div>
                    <h2 id="settlement-export-included-title">{t("money.settlements.exports.detail.includedTitle")}</h2>
                    {record.included_statement_ids.length ? (
                        <div className="settlement-result-links">
                            {record.included_statement_ids.map((statementId) => <Link key={statementId} to={`/settlements/${statementId}`} aria-label={t("money.settlements.exports.statement", {id: statementId})}>{t("money.settlements.exports.statement", {id: statementId})}</Link>)}
                        </div>
                    ) : <p className="admin-muted">{t("money.settlements.exports.detail.includedEmpty")}</p>}
                </div>
                <div>
                    <h2>{t("money.settlements.exports.detail.excludedTitle")}</h2>
                    {record.excluded_statement_ids.length ? <p className="settlement-export-id-list">{record.excluded_statement_ids.map((id) => `#${id}`).join(", ")}</p> : <p className="admin-muted">{t("money.settlements.exports.detail.excludedEmpty")}</p>}
                </div>
            </section>
        </div>
    );
};
